<template>
  <div class="sales-order-lines">

    <b-row class="mb-4">
      <!-- General information -->
      <b-col cols="12" lg="6">
        <b-card>
          <b-card-title class="text-uppercase">Informatie</b-card-title>
          <b-table-simple small>
            <colgroup>
              <col />
              <col />
            </colgroup>
            <b-tbody>
              <b-tr>
                <b-th>Ordernummer</b-th>
                <b-td>{{ salesOrder.identifier }}</b-td>
              </b-tr>
              <b-tr>
                <b-th>Geplaatst op</b-th>
                <b-td>{{ datetime(salesOrder.created_at) }}</b-td>
              </b-tr>
              <b-tr>
                <b-th>Aantal orderregels</b-th>
                <b-td>{{ salesOrderLinesCount }}</b-td>
              </b-tr>
              <b-tr>
                <b-th>Totaal aantal producten</b-th>
                <b-td>{{ salesOrderLinesProductCount }}</b-td>
              </b-tr>
              <b-tr v-if="grandTotal !== null">
                <b-th>Totaal</b-th>
                <b-td>{{ price(grandTotal) }}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-card>
      </b-col>
    </b-row>
    
    <b-row class="mb-4">
      <!-- List of sales lines -->
      <b-col cols="12">
        <b-card>
          <b-row>
            <b-col cols="12">
              <b-card-title class="text-uppercase">
                Oorspronkelijke orderregels
              </b-card-title>

            <b-row align-h="end" v-if="searchEnabled">
                <b-col cols="12" md="6" lg="4">
                <b-form-group>
                    <b-form-input
                    v-model="searchFilters.originalSalesOrderLines"
                    placeholder="Zoeken in deze tabel..."
                    />
                </b-form-group>
                </b-col>
            </b-row>

              <b-table
                :selectable="false"
                :items="salesOrderLinesOriginal"
                :fields="salesOrderLinesFields"
                :filter="searchFilters.originalSalesOrderLines"
                select-mode="single"
                stacked="sm"
              >
              </b-table>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="mb-4">
      <!-- List of extra sales lines -->
      <b-col cols="12">
        <b-card>
          <b-row>
            <b-col cols="12">
              <b-card-title class="text-uppercase">
                Toegevoegde orderregels
              </b-card-title>

              <b-row align-h="end" v-if="searchEnabled">
                  <b-col cols="12" md="6" lg="4">
                  <b-form-group>
                      <b-form-input
                      v-model="searchFilters.extraSalesOrderLines"
                      placeholder="Zoeken in deze tabel..."
                      />
                  </b-form-group>
                  </b-col>
              </b-row>

              <template v-if="extraSalesOrderLinesAvailable">
                <b-table
                  :selectable="false"
                  :items="salesOrderLinesExtra"
                  :fields="salesOrderLinesFieldsExtra"
                  :filter="searchFilters.extraSalesOrderLines"
                  select-mode="single"
                  stacked="sm"
                />
              </template>

              <template v-else>
                <p class="mt-3">Er zijn geen toegevoegde orderregels.</p>
              </template>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </div>

</template>

<script>
import _ from 'lodash';
import { date, datetime } from "@/helpers/date";
import { price } from "@/helpers/numbers";

export default {
  props: {
    salesOrder: {
      required: true,
    },
  },
  data() {
    return {
      searchEnabled: false, // Disabled for now.
      searchFilters: {
        originalSalesOrderLines: "",
        extraSalesOrderLines: "",
      },
      salesOrderLinesFields: [
        /**
         * @see https://bootstrap-vue.org/docs/components/table#fields-as-an-array-of-objects
         */
        {
          key: "product_identifier",
          label: "Artikel nr",
        },
        {
          key: "unit_gross_price",
          label: "Prijs p/s",
          formatter: price,
          sortable: true,
        },
        {
          key: "quantity",
          label: "Aantal",
          sortable: true,
        },
        {
          key: "line_gross_price",
          label: "Prijs totaal",
          formatter: price,
          sortable: true,
        },
        {
          key: "product_name",
          label: "Naam",
          sortable: true,
        },
      ],
    };
  },
  computed: {
    salesOrderLinesFieldsExtra() {
      // Computed property because you can't merge two data properties directly
      return [
        ...this.salesOrderLinesFields,
        {
          key: "created_at",
          label: "Toegevoegd op",
          formatter: datetime,
        },
        {
          key: "promised_delivery_date",
          label: "Beoogde leverdatum",
          formatter: date,
        },
      ];
    },

    // Get sales order lines with sales_order_lines.is_extra = 0
    salesOrderLinesOriginal() {
      return this.salesOrderLines({filter: { is_extra: 0 }});
    },

    // Get sales order lines with sales_order_lines.is_extra = 1
    salesOrderLinesExtra() {
      return this.salesOrderLines({filter: { is_extra: 1 }});
    },

    // Total number of sales order lines
    salesOrderLinesCount() {
      return this.salesOrder.sales_order_lines.length;
    },

    // Total number of extra sales order lines
    salesOrderLinesExtraCount() {
      return this.salesOrderLinesExtra.length;
    },

    // Total number of products in the sales order
    salesOrderLinesProductCount() {
      return this.salesOrder.sales_order_lines.reduce((acc, cur) => {
        return acc + parseInt(cur.quantity || 0);
      }, 0);
    },

    // Total price of all items in the sales order
    salesOrderLinesTotalPrice() {
      return this.salesOrder.sales_order_lines.reduce((acc, cur) => {
        return acc + parseFloat(cur.line_gross_price || 0);
      }, 0);
    },

    // Grand total
    grandTotal() {
        return _.get(this.salesOrder, "grand_total", null);
    },

    extraSalesOrderLinesAvailable() {
      return this.salesOrderLinesExtraCount !== 0;
    }
  },
  methods: {
    datetime,
    price,
    /**
     * Get the sales order lines, with optional filtering using lodash _.filter();
     * 
     * @see https://lodash.com/docs/4.17.15#filter
     * @param {object} options - options for this method
     * @param {object} options.filter - filter options passed to _.filter();
     * @returns {array} Sales order lines
     * @example salesOrderLines()
     * @example salesOrderLines({ filter: { is_extra: 1 } }
     * @example salesOrderLines({ filter: { product_name: "test" } })
     */
    salesOrderLines({ filter = {} }) {
      if (Object.keys(filter).length !== 0) {
        return _.filter(this.salesOrder.sales_order_lines, filter);
      }

      return this.salesOrder.sales_order_lines;
    },

  },
};
</script>